/**
 * Änderungsvorschlag als Karte: je Stelle der Wortvergleich (gestrichen /
 * neu) mit Stand, fürs Team „Annehmen" / „Ablehnen" und „Alle annehmen".
 * Dazu die Liste aller Vorschläge (Reiter „Änderungen").
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCheck, ChevronDown, ChevronUp, Copy, GitCompare, Info, Link as LinkIcon, Sparkles, Trash2, X } from 'lucide-react';
import { useToast } from '../Toast';
import { inZwischenablage, type FeedbackEintrag, type PlanungsDetail, type WerkstattAdapter } from '../../lib/planung';
import { wortDiff, type Entfernung } from '../../lib/zusammenarbeit';
import { formatVorschau, linksIn } from '../../lib/zusammenarbeit/formatieren';
import { DiffText, EintragAktionen, EintragKopf, EintragStatus } from './Teile';
import { useVorschlagUebernehmen, type Uebernahme } from './useVorschlagUebernehmen';
import { NUR_ABLEHNEN_TEXT, bewegungReduziert, diffKuerzen, vorschlagOffen, type WerkstattAktionen } from './werkstattHilfen';

type Stand = 'offen' | 'uebernommen' | 'abgelehnt' | 'erledigt';

const STAND: Record<Stand, { label: string; klasse: string }> = {
  offen: { label: 'offen', klasse: 'badge-neutral' },
  uebernommen: { label: 'übernommen', klasse: 'badge-success' },
  abgelehnt: { label: 'abgelehnt', klasse: 'badge-danger' },
  erledigt: { label: 'erledigt', klasse: 'badge-muted' },
};

type ZeileProps = {
  index: number;
  nr: number;
  vorher: string;
  nachher: string;
  nurFormat: boolean;
  /** Neue Fassung der Stelle mit Gestaltung (fett, Farbe, Link …), sonst null */
  nachherHtml?: string | null;
  /** Links, die es in der Mail noch nicht gibt (Adresse und Linktext) */
  neueLinks?: { href: string; text: string }[];
  /** Ganzer Abschnitt statt Textänderung */
  entfernen?: Entfernung | null;
  stand: Stand;
  u: Uebernahme;
};

function AenderungZeile({ index, nr, vorher, nachher, nurFormat, nachherHtml, neueLinks = [], entfernen, stand, u }: ZeileProps) {
  const toast = useToast();
  const [ganz, setGanz] = useState(false);
  const diff = useMemo(() => wortDiff(vorher, nachher), [vorher, nachher]);
  const vorschau = useMemo(() => (nachherHtml && !entfernen ? formatVorschau(nachherHtml) : null), [nachherHtml, entfernen]);
  const kurz = useMemo(() => diffKuerzen(diff), [diff]);
  const offen = stand === 'offen';
  const passtNicht = offen && u.passtNicht.has(index);
  const schonDrin = offen && u.schonDrin.has(index);
  const laeuft = u.laeuft !== null;
  const ort = index === -1 ? 'Betreff' : entfernen ? `Stelle ${nr} · ${entfernen.art} entfernen` : `Stelle ${nr}`;

  const kopieren = async () => {
    const ok = await inZwischenablage(nachher);
    if (ok) toast.success('Neuer Text kopiert.');
    else toast.error('Kopieren ging nicht — markiere den Text bitte von Hand.');
  };

  return (
    <li className={`za-aend za-aend--${stand}`}>
      <div className="za-aend-kopf">
        <span className="za-aend-ort">{ort}</span>
        <span className={`badge ${STAND[stand].klasse}`}>{STAND[stand].label}</span>
      </div>
      {entfernen && !vorher ? (
        <p className="za-diff"><Trash2 size={13} aria-hidden="true" /> {entfernen.bilder.length > 1 ? `Nur Bilder (${entfernen.bilder.length}), kein Text` : 'Nur ein Bild, kein Text'}</p>
      ) : (
        <p className="za-diff">
          <DiffText teile={ganz ? diff : kurz.teile} />
        </p>
      )}
      {kurz.gekuerzt && (
        <button type="button" className="za-link-knopf" onClick={(e) => { e.stopPropagation(); setGanz((g) => !g); }} aria-expanded={ganz}>
          {ganz ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
          {ganz ? 'Weniger zeigen' : 'Ganzen Text zeigen'}
        </button>
      )}
      {entfernen && (
        <p className="za-aend-info">
          <Trash2 size={13} aria-hidden="true" /> Der ganze Abschnitt fällt weg — mit Hintergrund, Rand und Abstand
          {entfernen.bilder.length ? ` und ${entfernen.bilder.length === 1 ? 'dem Bild' : `den ${entfernen.bilder.length} Bildern`}` : ''}.
        </p>
      )}
      {nurFormat && <p className="za-aend-info"><Info size={13} aria-hidden="true" /> Nur die Formatierung ist anders{vorschau ? '' : ', z. B. ein Zeilenumbruch'}.</p>}
      {vorschau && (
        <div className="za-aend-vorschau">
          <span className="za-aend-vorschau-titel">So sieht die Stelle danach aus:</span>
          {/* formatVorschau lässt nur Text-Auszeichnung durch (fett, kursiv, Farbe, Link) */}
          <p className="za-aend-vorschau-text" dangerouslySetInnerHTML={{ __html: vorschau }} />
        </div>
      )}
      {neueLinks.map((l) => (
        <p key={l.href} className="za-aend-info za-aend-link">
          <LinkIcon size={13} aria-hidden="true" />
          <span>
            Neuer Link{l.text ? <> auf „{l.text}“</> : null}:{' '}
            <a href={l.href} target="_blank" rel="noopener noreferrer">{l.href}</a>
          </span>
        </p>
      ))}
      {passtNicht && u.darfEntscheiden && !u.sperre && (
        <p className="za-aend-warnung" role="status">
          <AlertTriangle size={13} aria-hidden="true" /> Diese Stelle hat sich inzwischen geändert — bitte von Hand einarbeiten.
        </p>
      )}
      {schonDrin && u.darfEntscheiden && <p className="za-aend-info"><Check size={13} aria-hidden="true" /> Steht schon so in der Mail.</p>}
      {offen && u.darfEntscheiden && (
        <div className="za-aend-aktionen">
          {u.sperre === 'assistent' ? (
            entfernen ? null : (
              <button type="button" className="btn btn-sm" onClick={(e) => { e.stopPropagation(); void kopieren(); }}>
                <Copy size={14} aria-hidden="true" /> Neuen Text kopieren
              </button>
            )
          ) : u.sperre === 'rechte' ? null : (
            <button
              type="button"
              className="btn btn-sm za-knopf-annehmen"
              disabled={!!u.sperre || passtNicht || laeuft}
              title={u.sperre === 'versendet' ? 'Die Mail ist schon versendet.' : passtNicht ? 'Die Stelle hat sich inzwischen geändert.' : undefined}
              onClick={(e) => { e.stopPropagation(); void u.annehmen([index]); }}
            >
              {u.laeuft === `a${index}` ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
              {entfernen ? 'Entfernen' : 'Annehmen'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm za-knopf-ablehnen"
            disabled={laeuft}
            onClick={(e) => { e.stopPropagation(); void u.ablehnen(index); }}
          >
            {u.laeuft === `r${index}` ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <X size={14} aria-hidden="true" />}
            Ablehnen
          </button>
        </div>
      )}
    </li>
  );
}

type KarteProps = {
  eintrag: FeedbackEintrag;
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  aktionen: WerkstattAktionen;
  /** Eigene Karte mit Kopf (Reiter „Änderungen"); ohne: nur der Inhalt (in der Kommentarspalte) */
  mitKopf?: boolean;
  aktiv?: boolean;
  onWaehlen?: (id: string) => void;
};

/** Ein Änderungsvorschlag — Wortvergleich je Stelle, fürs Team mit Annehmen/Ablehnen. */
export function AenderungKarte({ eintrag, mail, adapter, aktionen, mitKopf, aktiv, onWaehlen }: KarteProps) {
  const u = useVorschlagUebernehmen(eintrag, mail, adapter, aktionen.erstFragen);
  const karteRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!aktiv || !mitKopf) return;
    karteRef.current?.scrollIntoView({ block: 'nearest', behavior: bewegungReduziert() ? 'auto' : 'smooth' });
  }, [aktiv, mitKopf]);
  const aenderungen = eintrag.aenderungen ?? [];
  const annehmbar = u.offeneIndizes.filter((i) => i === -1 || !u.passtNicht.has(i)).length;
  const offenGesamt = u.offeneIndizes.length;
  const standVon = (ergebnis: string | null | undefined): Stand => {
    if (ergebnis === 'uebernommen' || ergebnis === 'abgelehnt') return ergebnis;
    return eintrag.ergebnis ? (eintrag.ergebnis === 'erledigt' ? 'erledigt' : eintrag.ergebnis) : 'offen';
  };
  const veraltet = eintrag.basis_md5 !== null && eintrag.basis_md5 !== mail.md5 && !eintrag.ergebnis;
  // Links, die es in der Mail noch nicht gibt — das Team sieht die Adresse, bevor es annimmt
  const mailLinks = useMemo(() => new Set(linksIn(mail.html ?? '').map((l) => l.href)), [mail.html]);
  const neueLinks = (html: string | null | undefined) => (html ? linksIn(html).filter((l) => !mailLinks.has(l.href)) : []);
  const zeigeErledigen = u.darfEntscheiden && offenGesamt > 0 && (u.sperre !== null || u.passtNicht.size > 0);

  const inhalt = (
    <div className="za-vorschlag">
      {eintrag.text && <p className="za-text">{eintrag.text}</p>}
      {veraltet && eintrag.basis_version !== null && (
        <p className="za-vorschlag-basis">Gemacht auf Version {eintrag.basis_version} — die Mail hat sich seitdem geändert.</p>
      )}
      <ol className="za-aend-liste">
        {eintrag.betreff_vorschlag && (
          <AenderungZeile
            index={-1}
            nr={0}
            vorher={mail.betreff}
            nachher={eintrag.betreff_vorschlag}
            nurFormat={false}
            stand={standVon(eintrag.betreff_ergebnis)}
            u={u}
          />
        )}
        {aenderungen.map((a, i) => (
          <AenderungZeile
            key={i}
            index={i}
            nr={i + 1}
            vorher={a.vorher_text}
            nachher={a.nachher_text}
            nurFormat={!a.entfernen && a.vorher_text === a.nachher_text}
            nachherHtml={a.entfernen ? null : a.nachher_html}
            neueLinks={a.entfernen ? [] : neueLinks(a.nachher_html)}
            entfernen={a.entfernen}
            stand={standVon(a.ergebnis)}
            u={u}
          />
        ))}
      </ol>
      {u.darfEntscheiden && offenGesamt > 0 && u.sperre === 'assistent' && (
        <p className="za-sperre"><Sparkles size={14} aria-hidden="true" /> Diese Mail kommt aus dem Assistenten — bitte dort korrigieren{aenderungen.some((a) => a.entfernen) ? ' bzw. den Abschnitt entfernen' : ''}. Danach hier als erledigt markieren.</p>
      )}
      {u.darfEntscheiden && offenGesamt > 0 && u.sperre === 'versendet' && (
        <p className="za-sperre"><Info size={14} aria-hidden="true" /> Die Mail ist schon versendet — übernehmen geht nicht mehr.</p>
      )}
      {u.darfEntscheiden && offenGesamt > 0 && u.sperre === 'rechte' && (
        <p className="za-sperre"><Info size={14} aria-hidden="true" /> {NUR_ABLEHNEN_TEXT}</p>
      )}
      {(zeigeErledigen || (u.darfEntscheiden && !u.sperre && offenGesamt > 1)) && (
        <div className="za-vorschlag-fuss">
          {u.darfEntscheiden && !u.sperre && offenGesamt > 1 && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={u.laeuft !== null || annehmbar === 0}
              onClick={(e) => { e.stopPropagation(); void u.annehmen('alle'); }}
            >
              {u.laeuft === 'alle' ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <CheckCheck size={14} aria-hidden="true" />}
              Alle annehmen
            </button>
          )}
          {zeigeErledigen && (
            <button type="button" className="btn btn-sm" disabled={u.laeuft !== null} onClick={(e) => { e.stopPropagation(); void u.erledigen(); }}>
              Als erledigt markieren
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (!mitKopf) return inhalt;

  return (
    <article
      ref={karteRef}
      className={`za-karte za-karte--vorschlag${aktiv ? ' za-karte--aktiv' : ''}${eintrag.ergebnis ? ' za-karte--erledigt' : ''}`}
      aria-label={`Änderungsvorschlag von ${eintrag.autor_name}`}
      tabIndex={0}
      onClick={() => onWaehlen?.(eintrag.id)}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onWaehlen?.(eintrag.id); } }}
    >
      <EintragKopf eintrag={eintrag} adapter={adapter} />
      {inhalt}
      <EintragStatus eintrag={eintrag} />
      <EintragAktionen eintrag={eintrag} alle={mail.feedback} adapter={adapter} aktionen={aktionen} />
    </article>
  );
}

type ListeProps = {
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  aktionen: WerkstattAktionen;
  aktivId: string | null;
  onWaehlen: (id: string) => void;
  /** In der Mail zeigen (Modus „Änderungen") */
  onZeigen?: () => void;
  /** Bearbeiten starten */
  onBearbeiten?: () => void;
};

/** Reiter „Änderungen": alle Vorschläge, offene zuerst. */
export function AenderungenListe({ mail, adapter, aktionen, aktivId, onWaehlen, onZeigen, onBearbeiten }: ListeProps) {
  const [filter, setFilter] = useState<'offen' | 'alle'>('offen');
  const alle = useMemo(
    () => mail.feedback.filter((f) => f.art === 'vorschlag' && !f.antwort_auf).sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [mail.feedback],
  );
  const offen = useMemo(() => alle.filter(vorschlagOffen), [alle]);
  const liste = filter === 'offen' ? offen : alle;

  return (
    <div className="za-panel">
      <div className="za-panel-kopf">
        <div className="seg-control seg-control--sm" role="group" aria-label="Vorschläge filtern">
          <button type="button" className={`seg-btn${filter === 'offen' ? ' active' : ''}`} aria-pressed={filter === 'offen'} onClick={() => setFilter('offen')}>
            Offen <span className="za-zahl">{offen.length}</span>
          </button>
          <button type="button" className={`seg-btn${filter === 'alle' ? ' active' : ''}`} aria-pressed={filter === 'alle'} onClick={() => setFilter('alle')}>
            Alle <span className="za-zahl">{alle.length}</span>
          </button>
        </div>
        {onZeigen && offen.length > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onZeigen}>
            <GitCompare size={14} aria-hidden="true" /> In der Mail zeigen
          </button>
        )}
      </div>
      {liste.length === 0 ? (
        <div className="za-leer">
          <GitCompare size={22} aria-hidden="true" />
          <p className="za-leer-titel">{filter === 'offen' && alle.length ? 'Keine offenen Vorschläge' : 'Noch keine Änderungsvorschläge'}</p>
          <p className="za-leer-text">
            {filter === 'offen' && alle.length
              ? 'Alles entschieden. Unter „Alle“ stehen die bisherigen Vorschläge.'
              : 'Mit „Text ändern“ kannst Du Formulierungen direkt in der Mail ändern, mit „Abschnitt entfernen“ ganze Absätze oder Kästen herausnehmen — beides wird als Vorschlag gespeichert.'}
          </p>
          {onBearbeiten && !(filter === 'offen' && alle.length) && (
            <button type="button" className="btn btn-sm" onClick={onBearbeiten}>Text ändern</button>
          )}
        </div>
      ) : (
        <div className="za-karten">
          {liste.map((f) => (
            <AenderungKarte
              key={f.id}
              eintrag={f}
              mail={mail}
              adapter={adapter}
              aktionen={aktionen}
              mitKopf
              aktiv={f.id === aktivId}
              onWaehlen={onWaehlen}
            />
          ))}
        </div>
      )}
      {liste.length > 0 && adapter.art === 'link' && (
        <p className="za-panel-fuss">Das Team entscheidet, welche Vorschläge in die Mail kommen. Hier siehst Du, was übernommen wurde.</p>
      )}
    </div>
  );
}
